import { Strings } from 'src/app/common/utility/string/string';
import { Environment } from 'src/environments/environment';
import Char from 'typescript-char';
import { ApiController } from './api-controller';
import { ControllerEndpoint } from './controller-endpoint';
import { Route } from './routes';
import { Verb } from './verb';

enum Controller {
    Pool = 'pool',
    User = 'user',
    Users = 'users',
    Organization = 'organization',
    Logging = 'log',
    EdgeDevice = "edgeDevice",
    BatteryString = "batteryString"
}

export class Api {
    public static cognitoPool: ApiController = new ApiController(
        Controller.Pool, new Map<string, ControllerEndpoint>([
            [Route.Authentication.PoolId, Api.get('?organization={organization}')],
            [Route.Authentication.Policy, Api.get('{poolId}/policy')]
        ]),
        Environment.ApiUrl
    );

    public static user: ApiController = new ApiController(
        Controller.User, new Map<string, ControllerEndpoint>([
            [Route.User.Create, Api.put(Strings.Empty)],
            [Route.User.Update, Api.patch('{id}')],
            [Route.User.ToggleState, Api.get('{id}/state/{state}')],
            [Route.User.ResendPassword, Api.get('{username}/resendPassword')],
            [Route.User.EdgeDevices.Fetch, Api.get('{identifier}/edgeDevices')],
            [Route.User.EdgeDevices.Set, Api.put('{identifier}/edgeDevices')],
            // [Route.User.Sources.Fetch, Api.get('{id}/sources')],
            // [Route.User.Sources.Set, Api.put('{id}/sources')],
        ]),
        Environment.UserApi
    );

    public static users: ApiController = new ApiController(
        Controller.Users, new Map<string, ControllerEndpoint>([
            [Route.Users.List, Api.get(Strings.Empty)],
        ]),
        Environment.UserApi
    );

    public static organization: ApiController = new ApiController(
        Controller.Organization, new Map<string, ControllerEndpoint>([
            [Route.Organization.Information, Api.get('{identifier}')],
            [Route.Organization.Sources.Fetch, Api.get('{identifier}/sources')],
            [Route.Organization.Sources.Set, Api.put('{identifier}/sources')],
            [Route.Organization.EdgeDevices.Fetch, Api.get('{identifier}/edgeDevices')],
            [Route.Organization.EdgeDevices.Set, Api.put('{identifier}/edgeDevices')],
            [Route.Organization.List, Api.get(Strings.Empty)],
        ]),
        Environment.ApiUrl
    );

    public static logging: ApiController = new ApiController(
        Controller.Logging, new Map<string, ControllerEndpoint>([
            [Route.Logging.Send, Api.put('{logLevel}')],
        ]),
        Environment.ApiUrl
    );

    public static report: ApiController = new ApiController(
        Controller.Logging, new Map<string, ControllerEndpoint>([
            [Route.Report.EmbeddedUrl, Api.post(Strings.Empty)],
        ]),
        Environment.ApiUrl
    );

    public static edgeDevice: ApiController = new ApiController(
        Controller.EdgeDevice, new Map<string, ControllerEndpoint>([
            [Route.EdgeDevice.List, Api.get(Strings.Empty)],
            [Route.EdgeDevice.Update, Api.patch('{id}')]
        ]),
        Environment.EdgeDeviceApi
    );

    public static batteryString: ApiController = new ApiController(
        Controller.BatteryString, new Map<string, ControllerEndpoint>([
            [Route.BatteryString.Fetch, Api.get('{id}')],
            [Route.BatteryString.Set, Api.put('{id}')]
        ]),
        Environment.EdgeDeviceApi
    );

    public static getEndpoint(controller: ApiController, action: string): ControllerEndpoint {
        const returnValue: ControllerEndpoint | undefined = controller.endpoints.get(action)

        if (!returnValue) {
            throw new Error(`ApiController does not have action: '${action}'`);
        }

        return returnValue ?? new ControllerEndpoint(Strings.Empty, Verb.Get);
    }

    public static createParameters(route: string, values: Array<string>): Map<string, string> {
        const returnValue: Map<string, string> = new Map<string, string>();
        if (!!values) {

            const parameters = route.match(/{.+?}/g) ?? [];
            const loopLimit: number = values.length > parameters.length ? parameters.length : values.length;
            for (let i = 0; i < loopLimit; i++) {
                returnValue.set(parameters[i], values[i]);
            }
        }
        return returnValue;
    }

    public static getRoute(controller: ApiController, endpointName: string, parameters: Map<string, string>): string;
    public static getRoute(endpoint: ControllerEndpoint, parameters: Map<string, string>, controllerRoot: string): string;
    public static getRoute(
        controllerOrEndpoint: ApiController | ControllerEndpoint,
        endpointNameOrParameters: Map<string, string> | string,
        parametersOrControllerRoot: Map<string, string> | string
    ): string {
        if (
            controllerOrEndpoint instanceof ApiController &&
            typeof endpointNameOrParameters === 'string' &&
            parametersOrControllerRoot instanceof Map
        ) {
            const controller: ApiController = controllerOrEndpoint;
            const parameters: Map<string, string> = parametersOrControllerRoot;
            const endpointName: string = endpointNameOrParameters;
            const endpoint: ControllerEndpoint = Api.getEndpoint(controller, endpointName);

            return Api.getRoute(endpoint, parameters, controller.root);
        }

        if (
            controllerOrEndpoint instanceof ControllerEndpoint &&
            endpointNameOrParameters instanceof Map &&
            typeof parametersOrControllerRoot === 'string'
        ) {
            const endpoint: ControllerEndpoint = controllerOrEndpoint;
            const parameters: Map<string, string> = endpointNameOrParameters;
            const baseUrl: string = Strings.trimTrailing(parametersOrControllerRoot, Char.Slash);

            let returnValue: string = `${baseUrl}/${endpoint.route}`;

            parameters.forEach((value: string, key: string) => {
                returnValue = returnValue.replace(key, value);
            });

            return returnValue;
        }

        return Strings.Empty
    }


    private static create(route: string, verb: Verb = Verb.Get): ControllerEndpoint {
        return new ControllerEndpoint(route, verb);
    }

    private static get(route: string): ControllerEndpoint {
        return this.create(route);
    }

    private static post(route: string): ControllerEndpoint {
        return this.create(route, Verb.Post);
    }

    private static put(route: string): ControllerEndpoint {
        return this.create(route, Verb.Put);
    }

    private static patch(route: string): ControllerEndpoint {
        return this.create(route, Verb.Patch);
    }
}
